import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import Pagination from '@/components/base/Pagination';

interface Term {
  id: number;
  title: string;
  content: string;
  version: string;
  terms_type: string;
  is_active: boolean;
  created_at: string;
}

const TERMS_TYPE_LABELS: Record<string, string> = {
  service: '서비스 이용약관',
  privacy: '개인정보 처리방침',
};

const PAGE_SIZE = 10;

// 서버 RPC 오류를 안전한 일반 메시지로 변환(원문 SQL/DB 오류 미노출)
const termsErrorMessage = (message: string): string => {
  if (message.includes('version already exists')) return '이미 존재하는 버전입니다.';
  if (message.includes('cannot modify agreed terms')) return '동의한 사용자가 있어 내용을 수정할 수 없습니다.';
  if (message.includes('cannot delete active terms')) return '활성 약관은 삭제할 수 없습니다.';
  if (message.includes('terms has agreements')) return '동의한 사용자가 있어 삭제할 수 없습니다.';
  if (message.includes('terms not found') || message.includes('not found')) return '약관을 찾을 수 없습니다.';
  if (message.includes('forbidden')) return '권한이 없습니다.';
  if (message.includes('invalid')) return '입력값이 올바르지 않습니다.';
  return '요청 처리에 실패했습니다. 다시 시도해주세요.';
};

export default function TermsManager() {
  const [terms, setTerms] = useState<Term[]>([]);
  const [agreementCounts, setAgreementCounts] = useState<Record<number, number>>({});
  const [termsTypes, setTermsTypes] = useState<string[]>([]);
  const [activeTerms, setActiveTerms] = useState<Term[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedType, setSelectedType] = useState<string>('service');

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [editingTerm, setEditingTerm] = useState<Term | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);

  const [showNewVersion, setShowNewVersion] = useState(false);
  const [newVersionTitle, setNewVersionTitle] = useState('');
  const [newVersionContent, setNewVersionContent] = useState('');
  const [newVersionError, setNewVersionError] = useState('');

  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [activatingId, setActivatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // ── 읽기: 페이지네이션된 약관 목록 (selectedType 으로 서버 필터) ──
  const fetchTerms = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, count, error } = await supabase
        .from('terms')
        .select('*', { count: 'exact' })
        .eq('terms_type', selectedType)
        .order('created_at', { ascending: false })
        .range(from, to);
      if (error) throw error;
      setTerms((data as Term[]) || []);
      if (count !== null) setTotalCount(count);
    } catch (err) {
      if (import.meta.env.DEV) console.error('[TermsManager] 약관 조회 실패:', err);
      setLoadError('약관 데이터를 불러오지 못했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  }, [page, selectedType]);

  // ── 읽기: 동의 건수(서버 집계 — 반환 상한/페이지 잘림 방지) ──
  const fetchAgreementCounts = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_terms_agreement_counts');
      if (error) throw error;
      const map: Record<number, number> = {};
      (data || []).forEach((row: { terms_id: number; agreement_count: number }) => {
        map[row.terms_id] = Number(row.agreement_count) || 0;
      });
      setAgreementCounts(map);
    } catch (err) {
      if (import.meta.env.DEV) console.error('[TermsManager] 동의 건수 조회 실패:', err);
    }
  }, []);

  // ── 읽기: terms_type 목록(서버 집계 — 페이지 일부만으로 잘못 판정하지 않음) ──
  const fetchTypeSummary = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_terms_type_summary');
      if (error) throw error;
      setTermsTypes((data || []).map((row: { terms_type: string }) => row.terms_type));
    } catch (err) {
      if (import.meta.env.DEV) console.error('[TermsManager] terms_type 조회 실패:', err);
    }
  }, []);

  // ── 읽기: 활성 약관(전체 — 페이지에 종속되지 않도록 별도 조회) ──
  const fetchActiveTerms = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('terms').select('*').eq('is_active', true);
      if (error) throw error;
      setActiveTerms((data as Term[]) || []);
    } catch (err) {
      if (import.meta.env.DEV) console.error('[TermsManager] 활성 약관 조회 실패:', err);
    }
  }, []);

  const refreshMeta = useCallback(async () => {
    await Promise.all([fetchAgreementCounts(), fetchTypeSummary(), fetchActiveTerms()]);
  }, [fetchAgreementCounts, fetchTypeSummary, fetchActiveTerms]);

  useEffect(() => {
    fetchTerms();
    refreshMeta();
  }, [fetchTerms, refreshMeta]);

  const filteredTerms = terms;
  const activeTerm = activeTerms.find((t) => t.terms_type === selectedType);

  const getAgreementCount = (termsId: number) => agreementCounts[termsId] || 0;

  const handleStartEdit = (term: Term) => {
    const hasAgreements = getAgreementCount(term.id) > 0;
    if (hasAgreements) {
      // 동의 이력이 있으면 내용 수정 금지 → 새 버전 발행으로 유도
      setNewVersionTitle(term.title);
      setNewVersionContent(term.content);
      setShowNewVersion(true);
      return;
    }
    setEditingTerm(term);
    setEditTitle(term.title);
    setEditContent(term.content);
    setEditError('');
  };

  const handleSaveEdit = async () => {
    if (!editingTerm) return;
    if (!editTitle.trim() || !editContent.trim()) {
      setEditError('제목과 내용을 모두 입력해주세요.');
      return;
    }
    setSaving(true);
    setEditError('');
    try {
      const { data, error } = await supabase.rpc('edit_terms_version', {
        p_terms_id: editingTerm.id,
        p_title: editTitle.trim(),
        p_content: editContent.trim(),
      });
      if (error || !data) {
        if (import.meta.env.DEV) console.error('[TermsManager] 약관 수정 실패:', error?.message ?? 'no row');
        setEditError(termsErrorMessage(error?.message ?? ''));
        return;
      }
      // 서버 성공 후에만 모달 닫기 + 목록 갱신
      setEditingTerm(null);
      await fetchTerms();
      await refreshMeta();
    } catch (err) {
      if (import.meta.env.DEV) console.error('[TermsManager] 약관 수정 예외:', err);
      setEditError('저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishNewVersion = async () => {
    if (!newVersionTitle.trim() || !newVersionContent.trim()) {
      setNewVersionError('제목과 내용을 모두 입력해주세요.');
      return;
    }
    if (activeTerm && activeTerm.content === newVersionContent.trim()) {
      setNewVersionError('현재 활성 버전과 내용이 동일합니다. 내용을 변경해주세요.');
      return;
    }
    setSaving(true);
    setNewVersionError('');
    try {
      // 버전 계산은 서버에서 수행(페이지 잘림 방지)
      const { data: nextVersion, error: vError } = await supabase.rpc('get_next_terms_version', {
        p_terms_type: selectedType,
      });
      if (vError || !nextVersion) {
        if (import.meta.env.DEV) console.error('[TermsManager] 버전 계산 실패:', vError?.message);
        setNewVersionError('버전 계산에 실패했습니다. 다시 시도해주세요.');
        return;
      }

      const { data, error } = await supabase.rpc('publish_terms_version', {
        p_terms_type: selectedType,
        p_title: newVersionTitle.trim(),
        p_content: newVersionContent.trim(),
        p_version: nextVersion,
      });
      if (error || !data) {
        if (import.meta.env.DEV) console.error('[TermsManager] 약관 발행 실패:', error?.message ?? 'no row');
        setNewVersionError(termsErrorMessage(error?.message ?? ''));
        return;
      }
      // 서버 성공 후에만 모달 닫기 + 입력 초기화 + 목록 갱신
      setShowNewVersion(false);
      setNewVersionTitle('');
      setNewVersionContent('');
      await fetchTerms();
      await refreshMeta();
    } catch (err) {
      if (import.meta.env.DEV) console.error('[TermsManager] 약관 발행 예외:', err);
      setNewVersionError('발행에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (termId: number) => {
    setActivatingId(termId);
    setEditError('');
    try {
      const { data, error } = await supabase.rpc('set_active_terms_version', { p_terms_id: termId });
      if (error || !data) {
        if (import.meta.env.DEV) console.error('[TermsManager] 약관 활성화 실패:', error?.message ?? 'no row');
        setEditError(termsErrorMessage(error?.message ?? ''));
        return;
      }
      await fetchTerms();
      await refreshMeta();
    } catch (err) {
      if (import.meta.env.DEV) console.error('[TermsManager] 약관 활성화 예외:', err);
      setEditError('활성화에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setActivatingId(null);
    }
  };

  const handleDelete = async (termId: number) => {
    setDeletingId(termId);
    setEditError('');
    try {
      const { data, error } = await supabase.rpc('delete_terms_version', { p_terms_id: termId });
      if (error || !data) {
        if (import.meta.env.DEV) console.error('[TermsManager] 약관 삭제 실패:', error?.message ?? 'no row');
        setEditError(termsErrorMessage(error?.message ?? ''));
        return;
      }
      setDeleteConfirm(null);
      await fetchTerms();
      await refreshMeta();
    } catch (err) {
      if (import.meta.env.DEV) console.error('[TermsManager] 약관 삭제 예외:', err);
      setEditError('삭제에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 mx-auto mb-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-foreground-400 text-sm">약관 데이터를 불러오는 중...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-center py-12">
        <p className="text-accent-600 text-sm mb-4">{loadError}</p>
        <button
          onClick={fetchTerms}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-background-300 text-foreground-600 hover:bg-background-100 text-sm transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i> 다시 시도
        </button>
      </div>
    );
  }

  return (
    <div>
      <h3 className="font-heading text-lg text-foreground-800 mb-4">약관 관리</h3>

      {/* Terms type tabs */}
      <div className="flex gap-1 mb-6 border-b border-background-200 overflow-x-auto scrollbar-hide">
        {termsTypes.map((tt) => (
          <button
            key={tt}
            onClick={() => { setSelectedType(tt); setPage(1); setEditError(''); }}
            className={`px-3 md:px-4 py-2.5 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
              selectedType === tt
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-foreground-400 hover:text-foreground-600'
            }`}
          >
            {TERMS_TYPE_LABELS[tt] || tt}
          </button>
        ))}
      </div>

      {/* Action error */}
      {editError && (
        <div className="mb-4 p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
          <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
          <p className="text-accent-700 text-sm">{editError}</p>
        </div>
      )}

      {/* Active version display */}
      {activeTerm && (
        <div className="mb-6 p-4 rounded-xl bg-secondary-50 border border-secondary-200">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-check-double-line text-secondary-600"></i>
            <span className="text-sm font-medium text-secondary-700">현재 활성 버전</span>
            <span className="text-xs bg-secondary-200 text-secondary-700 px-2 py-0.5 rounded-full font-medium">
              {activeTerm.version}
            </span>
          </div>
          <p className="text-sm text-secondary-600">{activeTerm.title}</p>
          <p className="text-xs text-secondary-400 mt-1">
            동의자 {getAgreementCount(activeTerm.id)}명 · {new Date(activeTerm.created_at).toLocaleDateString('ko-KR')} 게시
          </p>
        </div>
      )}

      {/* New version button */}
      <div className="mb-5">
        <button
          onClick={() => {
            setShowNewVersion(true);
            setNewVersionTitle(activeTerm?.title || TERMS_TYPE_LABELS[selectedType] || '');
            setNewVersionContent(activeTerm?.content || '');
            setNewVersionError('');
          }}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          새 버전 발행
        </button>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead>
            <tr className="border-b border-background-200">
              <th className="text-left py-3 px-4 text-foreground-400 font-medium">버전</th>
              <th className="text-left py-3 px-4 text-foreground-400 font-medium">제목</th>
              <th className="text-left py-3 px-4 text-foreground-400 font-medium">상태</th>
              <th className="text-left py-3 px-4 text-foreground-400 font-medium">동의자</th>
              <th className="text-left py-3 px-4 text-foreground-400 font-medium">게시일</th>
              <th className="text-left py-3 px-4 text-foreground-400 font-medium">관리</th>
            </tr>
          </thead>
          <tbody>
            {filteredTerms.map((term) => {
              const agreeCount = getAgreementCount(term.id);
              return (
                <tr key={term.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                  <td className="py-2.5 px-4">
                    <span className="text-xs font-mono font-bold text-foreground-600">{term.version}</span>
                  </td>
                  <td className="py-2.5 px-4 text-foreground-700 text-xs max-w-[200px] truncate">
                    {term.title}
                  </td>
                  <td className="py-2.5 px-4">
                    {term.is_active ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                        활성
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-background-200 text-foreground-400">
                        비활성
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-foreground-500 text-xs">{agreeCount}명</td>
                  <td className="py-2.5 px-4 text-foreground-400 text-xs whitespace-nowrap">
                    {new Date(term.created_at).toLocaleDateString('ko-KR')}
                  </td>
                  <td className="py-2.5 px-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleStartEdit(term)}
                        disabled={activatingId === term.id || deletingId === term.id}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        title="수정"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      {!term.is_active && (
                        <button
                          onClick={() => handleSetActive(term.id)}
                          disabled={activatingId === term.id || deletingId === term.id}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-secondary-600 hover:bg-secondary-50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          title="활성화"
                        >
                          <i className={activatingId === term.id ? 'ri-loader-4-line animate-spin text-sm' : 'ri-check-line text-sm'}></i>
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteConfirm(term.id)}
                        disabled={activatingId === term.id || deletingId === term.id}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        title="삭제"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                    {deleteConfirm === term.id && (
                      <div className="mt-2 flex items-center gap-1.5">
                        <span className="text-xs text-foreground-400">삭제?</span>
                        <button
                          onClick={() => handleDelete(term.id)}
                          disabled={deletingId === term.id}
                          className="px-2 py-0.5 rounded text-xs bg-accent-500 text-white cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          {deletingId === term.id ? '삭제 중...' : '확인'}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          disabled={deletingId === term.id}
                          className="px-2 py-0.5 rounded text-xs border border-background-300 text-foreground-500 cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          취소
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {filteredTerms.map((term) => {
          const agreeCount = getAgreementCount(term.id);
          return (
            <div key={term.id} className="bg-background-50 rounded-xl border border-background-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono font-bold text-foreground-600">{term.version}</span>
                  {term.is_active ? (
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                      활성
                    </span>
                  ) : (
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-background-200 text-foreground-400">
                      비활성
                    </span>
                  )}
                </div>
                <span className="text-xs text-foreground-400">{agreeCount}명 동의</span>
              </div>
              <p className="text-sm text-foreground-700 mb-2 line-clamp-2">{term.title}</p>
              <p className="text-xs text-foreground-400 mb-3">
                {new Date(term.created_at).toLocaleDateString('ko-KR')}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleStartEdit(term)}
                  disabled={activatingId === term.id || deletingId === term.id}
                  className="px-3 py-1.5 rounded-lg text-xs border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  수정
                </button>
                {!term.is_active && (
                  <button
                    onClick={() => handleSetActive(term.id)}
                    disabled={activatingId === term.id || deletingId === term.id}
                    className="px-3 py-1.5 rounded-lg text-xs border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {activatingId === term.id ? '활성화 중...' : '활성화'}
                  </button>
                )}
                <button
                  onClick={() => setDeleteConfirm(term.id)}
                  disabled={activatingId === term.id || deletingId === term.id}
                  className="px-3 py-1.5 rounded-lg text-xs text-accent-500 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  삭제
                </button>
              </div>
              {deleteConfirm === term.id && (
                <div className="mt-2 flex items-center gap-1.5">
                  <span className="text-xs text-foreground-400">정말 삭제할까요?</span>
                  <button
                    onClick={() => handleDelete(term.id)}
                    disabled={deletingId === term.id}
                    className="px-2 py-0.5 rounded text-xs bg-accent-500 text-white cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {deletingId === term.id ? '삭제 중...' : '확인'}
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    disabled={deletingId === term.id}
                    className="px-2 py-0.5 rounded text-xs border border-background-300 text-foreground-500 cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    취소
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalCount={totalCount}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      {/* Edit modal */}
      {editingTerm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditingTerm(null)}></div>
          <div className="relative w-full max-w-2xl max-h-[85vh] bg-background-50 rounded-2xl overflow-hidden flex flex-col mx-4">
            <div className="flex items-center justify-between p-4 md:p-5 border-b border-background-200">
              <h2 className="font-heading text-lg text-foreground-800">
                약관 수정 ({editingTerm.version})
              </h2>
              <button
                onClick={() => setEditingTerm(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4">
              {editError && (
                <div className="p-3 rounded-lg bg-accent-50 border border-accent-200 text-accent-700 text-sm">
                  {editError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">제목</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">내용</label>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={12}
                  className="w-full px-4 py-2 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                />
              </div>
            </div>
            <div className="p-4 md:p-5 border-t border-background-200 flex gap-2 justify-end">
              <button
                onClick={() => setEditingTerm(null)}
                disabled={saving}
                className="px-5 py-2 rounded-lg border border-background-300 text-foreground-500 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="px-5 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New version modal */}
      {showNewVersion && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowNewVersion(false)}></div>
          <div className="relative w-full max-w-2xl max-h-[85vh] bg-background-50 rounded-2xl overflow-hidden flex flex-col mx-4">
            <div className="flex items-center justify-between p-4 md:p-5 border-b border-background-200">
              <h2 className="font-heading text-lg text-foreground-800">
                새 버전 발행 - {TERMS_TYPE_LABELS[selectedType] || selectedType}
              </h2>
              <button
                onClick={() => setShowNewVersion(false)}
                disabled={saving}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4">
              {newVersionError && (
                <div className="p-3 rounded-lg bg-accent-50 border border-accent-200 text-accent-700 text-sm">
                  {newVersionError}
                </div>
              )}
              <div className="p-3 rounded-lg bg-primary-50 border border-primary-200 text-primary-700 text-sm">
                <i className="ri-information-line mr-1"></i>
                새 버전을 발행하면 기존 활성 버전은 자동으로 비활성화되고, 사용자들은 다음 로그인 시 새 약관에 재동의해야 합니다.
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">제목</label>
                <input
                  type="text"
                  value={newVersionTitle}
                  onChange={(e) => setNewVersionTitle(e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">내용</label>
                <textarea
                  value={newVersionContent}
                  onChange={(e) => setNewVersionContent(e.target.value)}
                  rows={12}
                  className="w-full px-4 py-2 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                />
              </div>
            </div>
            <div className="p-4 md:p-5 border-t border-background-200 flex gap-2 justify-end">
              <button
                onClick={() => setShowNewVersion(false)}
                disabled={saving}
                className="px-5 py-2 rounded-lg border border-background-300 text-foreground-500 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={handlePublishNewVersion}
                disabled={saving}
                className="px-5 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {saving ? '발행 중...' : '새 버전 발행'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}